import clsx from 'clsx'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

// Components and Icons
import { CircularProgress, ThemeProvider, createTheme } from '@mui/material'
import { CheckCircle, XCircle } from '../../assets/icons/index.ts'

// Constants and types
import { VALID_COMBINATIONS, BUTTON_VARIANTS, BUTTON_SIZES, BUTTON_COLORS } from './constants'
import type { ButtonVariant, ButtonColor, ButtonSize, LoadingState } from './types'

// ============================================================================
// THEMES
// ============================================================================

const spinnerTheme = createTheme({
  palette: {
    primary: {
      main: '#fff',
    },
  },
})

// ============================================================================
// STYLE DEFINITIONS
// ============================================================================

/**
 * Style matrix: variant × color
 * Only includes valid combinations per design system
 * Each combination has default, hover, disabled, loading, and error states
 */
const typeColorStyles = {
  filled: {
    primary: clsx(
      'bg-brand-500 text-white shadow-sm',
      'hover:bg-brand-800 active:bg-brand-800',
      'focus-visible:outline-brand-500',
      'disabled:bg-true-gray-300 disabled:text-white',
      '[&[data-loading-state="true"]]:bg-brand-800 [&[data-loading-state="true"]]:text-white'
    ),
    destructive: clsx(
      'bg-red-700 text-white shadow-sm',
      'hover:bg-red-900 active:bg-red-900',
      'focus-visible:outline-red-700',
      'disabled:bg-true-gray-300 disabled:text-white',
      '[&[data-loading-state="true"]]:bg-red-800 [&[data-loading-state="true"]]:text-white'
    ),
  },
  outlined: {
    primary: clsx(
      'bg-transparent text-brand-500',
      'ring-1 ring-inset ring-brand-500',
      'hover:bg-brand-neutral-100 active:bg-brand-neutral-100',
      'focus-visible:outline-brand-500',
      'disabled:text-true-gray-300 disabled:ring-true-gray-300 disabled:bg-transparent',
      '[&[data-loading-state="true"]]:bg-neutral-100 [&[data-loading-state="true"]]:text-brand-600 [&[data-loading-state="true"]]:ring-brand-600'
    ),
    neutral: clsx(
      'bg-transparent text-brand-neutral-800',
      'ring-1 ring-inset ring-brand-neutral-600',
      'hover:bg-brand-neutral-100 active:bg-brand-neutral-100',
      'focus-visible:outline-brand-neutral-500',
      'disabled:text-true-gray-300 disabled:ring-true-gray-300 disabled:bg-transparent',
      '[&[data-loading-state="true"]]:bg-neutral-100 [&[data-loading-state="true"]]:text-brand-neutral-700 [&[data-loading-state="true"]]:ring-brand-neutral-500'
    ),
    destructive: clsx(
      'bg-transparent text-red-700',
      'ring-1 ring-inset ring-red-700',
      'hover:bg-brand-neutral-100 active:bg-brand-neutral-100',
      'focus-visible:outline-red-700',
      'disabled:text-true-gray-300 disabled:ring-true-gray-300 disabled:bg-transparent',
      '[&[data-loading-state="true"]]:bg-neutral-100 [&[data-loading-state="true"]]:text-red-800 [&[data-loading-state="true"]]:ring-red-800'
    ),
  },
  tonal: {
    primary: clsx(
      'bg-brand-100 text-brand-800',
      'ring-1 ring-inset ring-brand-200',
      'hover:bg-brand-200 active:bg-brand-200',
      'focus-visible:outline-brand-500',
      'disabled:bg-true-gray-100 disabled:text-true-gray-300 disabled:ring-true-gray-300',
      '[&[data-loading-state="true"]]:bg-brand-200 [&[data-loading-state="true"]]:text-brand-800 [&[data-loading-state="true"]]:ring-brand-200'
    ),
    neutral: clsx(
      'bg-brand-neutral-200 text-brand-neutral-950',
      'ring-1 ring-inset ring-brand-neutral-300',
      'hover:bg-brand-neutral-300 active:bg-brand-neutral-300',
      'focus-visible:outline-brand-neutral-500',
      'disabled:bg-true-gray-100 disabled:text-true-gray-300 disabled:ring-true-gray-300',
      '[&[data-loading-state="true"]]:bg-brand-neutral-300 [&[data-loading-state="true"]]:text-brand-neutral-950 [&[data-loading-state="true"]]:ring-brand-neutral-300'
    ),
    destructive: clsx(
      'bg-red-100 text-red-800',
      'ring-1 ring-inset ring-red-200',
      'hover:bg-red-200 active:bg-red-200',
      'focus-visible:outline-red-800',
      'disabled:bg-true-gray-100 disabled:text-true-gray-300 disabled:ring-true-gray-300',
      '[&[data-loading-state="true"]]:bg-red-200 [&[data-loading-state="true"]]:text-red-800 [&[data-loading-state="true"]]:ring-red-300'
    ),
    secondary: clsx(
      'bg-true-gray-200 text-true-gray-950',
      'ring-1 ring-inset ring-true-gray-300',
      'hover:bg-true-gray-300 active:bg-true-gray-300',
      'focus-visible:outline-true-gray-950',
      'disabled:bg-true-gray-100 disabled:text-true-gray-300 disabled:ring-true-gray-300',
      '[&[data-loading-state="true"]]:bg-true-gray-300 [&[data-loading-state="true"]]:text-true-gray-950 [&[data-loading-state="true"]]:ring-true-gray-400'
    ),
  },
  plain: {
    primary: clsx(
      'bg-transparent text-brand-500',
      'hover:bg-brand-100 active:bg-brand-100',
      'focus-visible:outline-brand-500',
      'disabled:text-true-gray-300 disabled:bg-transparent',
      '[&[data-loading-state="true"]]:text-brand-800'
    ),
    secondary: clsx(
      'bg-transparent text-brand-neutral-950',
      'hover:bg-brand-neutral-200 active:bg-brand-neutral-200',
      'focus-visible:outline-brand-neutral-900',
      'disabled:text-true-gray-300 disabled:bg-transparent',
      '[&[data-loading-state="true"]]:text-brand-neutral-950'
    ),
    neutral: clsx(
      'bg-transparent text-brand-neutral-600',
      'hover:bg-brand-neutral-100 active:bg-brand-neutral-100',
      'focus-visible:outline-brand-neutral-500',
      'disabled:text-true-gray-300 disabled:bg-transparent',
      '[&[data-loading-state="true"]]:text-brand-neutral-950'
    ),
    destructive: clsx(
      'bg-transparent text-red-700',
      'hover:bg-red-100 active:bg-red-100',
      'focus-visible:outline-red-700',
      'disabled:text-true-gray-300 disabled:bg-transparent',
      '[&[data-loading-state="true"]]:text-brand-neutral-950'
    ),
  },
} as const satisfies Record<ButtonVariant, Partial<Record<ButtonColor, string>>>

/**
 * Size styles for text buttons (base styles without padding)
 * Heights: md=36px, sm=28px, xs=24px
 */
const baseSizeStyles: Record<ButtonSize, string> = {
  xs: 'text-xs font-medium py-1 rounded h-[24px]',
  sm: 'text-sm font-semibold py-1 rounded-md h-[28px]',
  md: 'text-sm font-semibold py-2 rounded-md h-[36px]',
}

/**
 * Padding styles for non-plain buttons (varies by size)
 */
const paddingWithoutIcons: Record<ButtonSize, string> = {
  xs: 'px-2',
  sm: 'px-3',
  md: 'px-4',
}

const paddingWithLeadingIcon: Record<ButtonSize, string> = {
  xs: 'pl-2 pr-2',
  sm: 'pl-[10px] pr-3',
  md: 'pl-[14px] pr-4',
}

const paddingWithTrailingIcon: Record<ButtonSize, string> = {
  xs: 'pl-2 pr-2',
  sm: 'pl-3 pr-[10px]',
  md: 'pl-4 pr-[14px]',
}

const paddingWithBothIcons: Record<ButtonSize, string> = {
  xs: 'px-2',
  sm: 'pl-[10px] pr-[10px]',
  md: 'pl-[14px] pr-[14px]',
}

/**
 * Padding styles for plain buttons (same for all sizes)
 */
const plainPaddingWithoutIcons = 'px-2'
const plainPaddingWithLeadingIcon = 'pl-1.5 pr-2'
const plainPaddingWithTrailingIcon = 'pl-2 pr-1.5'
const plainPaddingWithBothIcons = 'pl-1.5 pr-1.5'

/**
 * Base styles applied to all buttons
 */
const baseStyles = clsx(
  'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200',
  'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'disabled:cursor-not-allowed'
)

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Union of all valid variant/color combinations
 * Derived from VALID_COMBINATIONS to keep them in sync
 */
type ValidButtonCombination =
  | { variant?: 'filled'; color?: (typeof VALID_COMBINATIONS)['filled'][number] }
  | { variant?: 'outlined'; color?: (typeof VALID_COMBINATIONS)['outlined'][number] }
  | { variant?: 'tonal'; color?: (typeof VALID_COMBINATIONS)['tonal'][number] }
  | { variant?: 'plain'; color?: (typeof VALID_COMBINATIONS)['plain'][number] }
  | { variant?: never; color?: (typeof VALID_COMBINATIONS)['filled'][number] } // default variant is 'filled'

/**
 * Base button props shared across all variants
 */
interface BaseButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'children'> {
  /**
   * Button text content (required — use the IconButton component for icon-only buttons).
   */
  children: ReactNode
  /**
   * The size of the button
   * @default 'md'
   */
  size?: ButtonSize
  /**
   * Icon to display before the button text
   */
  leadingIcon?: ReactNode
  /**
   * Icon to display after the button text
   */
  trailingIcon?: ReactNode
  /**
   * Whether the button should take up the full width of its container
   * @default false
   */
  fullWidth?: boolean
  /**
   * Loading state - can be boolean or specific state
   * - false: Not loading
   * - true | 'loading': Shows spinner
   * - 'success': Shows checkmark icon
   * - 'error': Shows X icon
   * @default false
   */
  loading?: LoadingState
}

/**
 * Button props with type-safe variant/color combinations
 * TypeScript will enforce valid color combinations based on the variant
 */
export type ButtonProps = BaseButtonProps & ValidButtonCombination

/**
 * Get padding style based on variant, size, and icon presence
 */
function getPaddingStyle(variant: ButtonVariant, size: ButtonSize, hasLeadingIcon: boolean, hasTrailingIcon: boolean): string {
  if (variant === BUTTON_VARIANTS.PLAIN) {
    if (!hasLeadingIcon && !hasTrailingIcon) return plainPaddingWithoutIcons
    if (hasLeadingIcon && hasTrailingIcon) return plainPaddingWithBothIcons
    if (hasLeadingIcon) return plainPaddingWithLeadingIcon
    return plainPaddingWithTrailingIcon
  }

  // Non-plain button padding (varies by size)
  if (!hasLeadingIcon && !hasTrailingIcon) return paddingWithoutIcons[size]
  if (hasLeadingIcon && hasTrailingIcon) return paddingWithBothIcons[size]
  if (hasLeadingIcon) return paddingWithLeadingIcon[size]
  return paddingWithTrailingIcon[size]
}

/**
 * Button Component
 *
 * A flexible, accessible button component following the Paxton design system.
 *
 * Valid variant/color combinations:
 * - filled: primary, destructive
 * - outlined: primary, neutral, destructive
 * - tonal: primary, neutral, destructive, secondary
 * - plain: primary, secondary, neutral, destructive (all)
 *
 * @example
 * // Primary action
 * <Button>Save</Button>
 *
 * @example
 * // Secondary/cancel action
 * <Button variant="outlined">Cancel</Button>
 *
 * @example
 * // Destructive action
 * <Button color="destructive">Delete</Button>
 *
 * @example
 * // Text link style
 * <Button variant="plain">Learn more</Button>
 *
 * @example
 * // With icons
 * <Button leadingIcon={<PlusIcon />}>Add Item</Button>
 * <Button trailingIcon={<ArrowRightIcon />}>Continue</Button>
 *
 * For icon-only buttons, use the IconButton component instead.
 *
 * @example
 * // Loading states
 * <Button loading>Saving...</Button>
 * <Button loading="success">Saved!</Button>
 * <Button loading="error">Failed</Button>
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    className,
    color = BUTTON_COLORS.PRIMARY,
    disabled,
    fullWidth = false,
    leadingIcon,
    loading = false,
    size = BUTTON_SIZES.MD,
    trailingIcon,
    variant = BUTTON_VARIANTS.FILLED,
    ...props
  },
  ref
) {
  // Runtime validation as a safety net (catches edge cases, dynamic values, etc.)
  // TypeScript types catch most issues at compile time, but this provides runtime safety
  if (process.env.NODE_ENV !== 'production' && !VALID_COMBINATIONS[variant].includes(color)) {
    const validColors = VALID_COMBINATIONS[variant].join(', ')
    console.error(
      `[Button] Invalid variant/color combination: variant="${variant}" with color="${color}". ` +
        `Valid colors for "${variant}" are: ${validColors}. ` +
        `This should have been caught by TypeScript. Falling back to default color.`
    )
    // Fallback to first valid color for the variant
    color = VALID_COMBINATIONS[variant][0] as ButtonColor
  }

  const isLoading = loading === true || loading === 'loading'
  const isSuccess = loading === 'success'
  const isError = loading === 'error'
  const isDisabled = disabled || isLoading

  const buttonStyles = typeColorStyles[variant][color as keyof (typeof typeColorStyles)[typeof variant]]

  // Add data attribute for loading/success/error states to enable CSS targeting
  const hasLoadingState = isLoading || isSuccess || isError
  const buttonProps = {
    ...props,
    ...(hasLoadingState && { 'data-loading-state': 'true' }),
  }

  // Determine icon size based on button size
  const iconSize = size === BUTTON_SIZES.SM || size === BUTTON_SIZES.XS ? 'h-4 w-4' : 'h-5 w-5'

  // Render loading/success/error icon
  const renderStateIcon = () => {
    if (isLoading) {
      const spinnerColor = 'text-current'
      const spinnerSize = size === BUTTON_SIZES.SM || size === BUTTON_SIZES.XS ? 13 : 16

      return (
        <span className={clsx('inline-flex items-center justify-center', iconSize, spinnerColor)}>
          <ThemeProvider theme={spinnerTheme}>
            <CircularProgress size={spinnerSize} thickness={7} sx={{ color: 'currentColor' }} />
          </ThemeProvider>
        </span>
      )
    }

    if (isSuccess) return <CheckCircle className={iconSize} />

    if (isError) {
      return <XCircle className={iconSize} />
    }

    return null
  }

  const dsAttributes = {
    'data-ds-component': 'Button',
    'data-ds-variant': variant,
    'data-ds-color': color,
    'data-ds-size': size,
  }

  // Determine which icons are present (loading/success/error appear in trailing position)
  const hasLeadingIcon = !!(leadingIcon && !loading)
  const hasTrailingIcon = !!(trailingIcon || loading)

  // Get padding style based on variant, size, and icon presence
  const paddingStyle = getPaddingStyle(variant, size, hasLeadingIcon, hasTrailingIcon)

  return (
    <button
      ref={ref}
      className={clsx(baseStyles, buttonStyles, baseSizeStyles[size], paddingStyle, fullWidth && 'w-full', className)}
      disabled={isDisabled}
      {...dsAttributes}
      {...buttonProps}
    >
      {hasLeadingIcon && <span className="inline-flex shrink-0">{leadingIcon}</span>}
      {children}
      {loading ? (
        <span className="inline-flex shrink-0">{renderStateIcon()}</span>
      ) : (
        trailingIcon && <span className="inline-flex shrink-0">{trailingIcon}</span>
      )}
    </button>
  )
})

export { Button }
